import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Room, RoomEvent } from 'livekit-client';
import { VoiceParticipant, UserData } from '../types';
import { MicIcon, VolumeOffIcon, CloseIcon, CameraIcon, ScreenShareIcon, FlipCameraIcon } from './Icons';
import { requestMicrophonePermission, checkSecureContext } from '../utils/permissionUtils';

export interface RemoteStreamViewProps {
    stream: MediaStream | null;
    isLocal?: boolean;
    muted?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

export const RemoteStreamView = React.memo(({ stream, isLocal, muted, className, style }: RemoteStreamViewProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (video && stream) {
            if (video.srcObject !== stream) {
                video.srcObject = stream;
            }
            video.play().catch(err => {
                console.warn('[RemoteStreamView] play() failed/prevented:', err);
            });
        }
        return () => {
            if (video) {
                video.srcObject = null;
            }
        };
    }, [stream]);

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={true}
            className={className}
            style={style}
        />
    );
});

export const RemoteAudioPlayer = React.memo(({ stream, muted }: { stream: MediaStream | null; muted?: boolean }) => {
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio && stream) {
            if (audio.srcObject !== stream) {
                audio.srcObject = stream;
            }
        }
        return () => {
            if (audio) {
                audio.srcObject = null;
            }
        };
    }, [stream]);

    return (
        <audio
            ref={audioRef}
            autoPlay
            playsInline
            muted={muted}
            style={{ display: 'none' }}
        />
    );
});

interface VoiceRoomProps {
    communityId: string;
    channelId: string;
    currentUser: string;
    usersData: Record<string, UserData>;
    socket: any;
    themeColor?: string;
    onLeave?: () => void;
    onRemoteStreamsUpdate?: (streams: Record<string, MediaStream>) => void;
    onLocalStreamUpdate?: (stream: MediaStream | null) => void;
    isMuted?: boolean;
    setIsMuted?: (muted: boolean) => void;
    isDeafened?: boolean;
    setIsDeafened?: (deafened: boolean) => void;
}

export const VoiceRoom: React.FC<VoiceRoomProps> = ({ 
    communityId, channelId, currentUser, usersData, socket, 
    themeColor = '#2563eb', onLeave, onRemoteStreamsUpdate, onLocalStreamUpdate,
    isMuted: propIsMuted, setIsMuted: propSetIsMuted,
    isDeafened: propIsDeafened, setIsDeafened: propSetIsDeafened
}) => {
    const [participants, setParticipants] = useState<VoiceParticipant[]>([]);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [localMuted, setLocalMuted] = useState(false);
    const [localDeafened, setLocalDeafened] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
    const [focusedParticipant, setFocusedParticipant] = useState<VoiceParticipant | null>(null);

    const isMuted = propIsMuted !== undefined ? propIsMuted : localMuted;
    const setIsMuted = propSetIsMuted !== undefined ? propSetIsMuted : setLocalMuted;
    const isDeafened = propIsDeafened !== undefined ? propIsDeafened : localDeafened;
    const setIsDeafened = propSetIsDeafened !== undefined ? propSetIsDeafened : setLocalDeafened;

    const roomRef = useRef<Room | null>(null);
    const [remoteStreamsMap, setRemoteStreamsMap] = useState<Record<string, MediaStream>>({});
    const [micError, setMicError] = useState<string | null>(null);

    const isMutedRef = useRef(isMuted);
    const isDeafenedRef = useRef(isDeafened);
    const isCameraOnRef = useRef(isCameraOn);
    const isScreenSharingRef = useRef(isScreenSharing);
    const participantsRef = useRef(participants);

    useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
    useEffect(() => { isDeafenedRef.current = isDeafened; }, [isDeafened]);
    useEffect(() => { isCameraOnRef.current = isCameraOn; }, [isCameraOn]);
    useEffect(() => { isScreenSharingRef.current = isScreenSharing; }, [isScreenSharing]);
    useEffect(() => { participantsRef.current = participants; }, [participants]);

    useEffect(() => {
        if (onLocalStreamUpdate) onLocalStreamUpdate(localStream);
    }, [localStream, onLocalStreamUpdate]);

    useEffect(() => {
        if (onRemoteStreamsUpdate) onRemoteStreamsUpdate(remoteStreamsMap);
    }, [remoteStreamsMap, onRemoteStreamsUpdate]);

    const effectiveMuted = isMuted || isDeafened;

    // Sync remote streams whenever participants change
    useEffect(() => {
        if (!roomRef.current) return;
        
        const newMap: Record<string, MediaStream> = {};
        roomRef.current.remoteParticipants.forEach((participant) => {
            const matchedVoiceUser = participants.find(p => p.username.toLowerCase() === participant.identity.toLowerCase());
            if (!matchedVoiceUser) return;

            const mediaStream = new MediaStream();
            participant.trackPublications.forEach((pub) => {
                const track = pub.track || pub.videoTrack || pub.audioTrack;
                if (track && track.mediaStreamTrack) {
                    mediaStream.addTrack(track.mediaStreamTrack);
                }
            });

            if (mediaStream.getTracks().length > 0) {
                newMap[matchedVoiceUser.socketId] = mediaStream;
            }
        });
        setRemoteStreamsMap(newMap);
    }, [participants]);

    useEffect(() => {
        // Emit join immediately so presence list is populated right away
        socket.emit('join-voice-room', { communityId, channelId, username: currentUser });

        const initLiveKit = async () => {
            try {
                await requestMicrophonePermission();

                // Fetch LiveKit Room Token
                const roomName = `${communityId}:${channelId}`;
                const res = await fetch(`/api/livekit/token?room=${encodeURIComponent(roomName)}&identity=${encodeURIComponent(currentUser)}`);
                if (!res.ok) throw new Error('Failed to generate LiveKit token');
                const data = await res.json();

                const room = new Room({
                    adaptiveStream: true,
                    dynacast: true
                });
                roomRef.current = room;

                const updateLocalStream = () => {
                    const tracks = Array.from(room.localParticipant.trackPublications.values())
                        .map(pub => pub.track)
                        .filter(t => t && t.mediaStreamTrack);
                    if (tracks.length > 0) {
                        const stream = new MediaStream();
                        tracks.forEach(t => {
                            if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                        });
                        setLocalStream(stream);
                    } else {
                        setLocalStream(null);
                    }
                };

                const updateRemoteStreams = () => {
                    const newMap: Record<string, MediaStream> = {};
                    room.remoteParticipants.forEach((participant) => {
                        const matchedVoiceUser = participantsRef.current.find(p => p.username.toLowerCase() === participant.identity.toLowerCase());
                        if (!matchedVoiceUser) return;

                        const mediaStream = new MediaStream();
                        participant.trackPublications.forEach((pub) => {
                            const track = pub.track || pub.videoTrack || pub.audioTrack;
                            if (track && track.mediaStreamTrack) {
                                mediaStream.addTrack(track.mediaStreamTrack);
                            }
                        });

                        if (mediaStream.getTracks().length > 0) {
                            newMap[matchedVoiceUser.socketId] = mediaStream;
                        }
                    });
                    setRemoteStreamsMap(newMap);
                };

                room.on(RoomEvent.LocalTrackPublished, updateLocalStream);
                room.on(RoomEvent.LocalTrackUnpublished, updateLocalStream);
                room.on(RoomEvent.TrackSubscribed, updateRemoteStreams);
                room.on(RoomEvent.TrackUnsubscribed, updateRemoteStreams);
                room.on(RoomEvent.ParticipantConnected, updateRemoteStreams);
                room.on(RoomEvent.ParticipantDisconnected, updateRemoteStreams);

                room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
                    const speakingMap: Record<string, boolean> = {};
                    speakers.forEach(s => {
                        speakingMap[s.identity] = true;
                    });
                    
                    setParticipants(prev => prev.map(p => ({
                        ...p,
                        isSpeaking: !!speakingMap[p.username]
                    })));

                    const isLocalSpeaking = !!speakingMap[currentUser];
                    socket.emit('voice-state-update', { isSpeaking: isLocalSpeaking });
                });

                await room.connect(data.url, data.token);
                console.log('[LiveKit] Connected successfully to room:', room.name);

                // Publish initial audio stream
                await room.localParticipant.setMicrophoneEnabled(!effectiveMuted);

            } catch (err: any) {
                console.error('[LiveKit] Connection error:', err);
                const secureCheck = checkSecureContext();
                if (!secureCheck.isSecure) {
                    setMicError(secureCheck.message);
                } else {
                    setMicError(`Could not access microphone (${err.name}). Please check hardware/settings.`);
                }
            }
        };

        initLiveKit();

        socket.on('voice-participants-list', (list: VoiceParticipant[]) => {
            setParticipants(list);
            socket.emit('voice-state-update', {
                isMuted: isMutedRef.current || isDeafenedRef.current,
                isDeafened: isDeafenedRef.current,
                isCameraOn: isCameraOnRef.current,
                isScreenSharing: isScreenSharingRef.current
            });
        });

        socket.on('user-joined-voice', ({ username, socketId }: { username: string, socketId: string }) => {
            console.log(`[VoiceRoom] user-joined-voice: ${username} joined with socketId ${socketId}`);
            setParticipants(prev => {
                const existing = prev.find(p => p.username === username);
                if (existing) {
                    return prev.map(p => p.username === username ? { ...p, socketId, isMuted: false, isDeafened: false, isSpeaking: false } : p);
                }
                return [...prev, { username, socketId, isMuted: false, isDeafened: false, isSpeaking: false }];
            });
        });

        socket.on('user-left-voice', ({ username, socketId }: { username: string, socketId: string }) => {
            setParticipants(prev => prev.filter(p => p.socketId !== socketId));
        });

        socket.on('voice-state-update', ({ socketId, state }: { socketId: string, state: any }) => {
            setParticipants(prev => prev.map(p => p.socketId === socketId ? { ...p, ...state } : p));
        });

        return () => {
            socket.emit('leave-voice-room');
            socket.off('voice-participants-list');
            socket.off('user-joined-voice');
            socket.off('user-left-voice');
            socket.off('voice-state-update');

            if (roomRef.current) {
                roomRef.current.disconnect();
                roomRef.current = null;
            }
        };
    }, [communityId, channelId, currentUser]);

    // Update microphone mute status dynamically
    useEffect(() => {
        if (roomRef.current) {
            roomRef.current.localParticipant.setMicrophoneEnabled(!effectiveMuted);
        }
        socket.emit('voice-state-update', { isMuted: effectiveMuted, isDeafened: isDeafened });
        setParticipants(prev => prev.map(p => p.username === currentUser ? { ...p, isMuted: effectiveMuted, isDeafened: isDeafened } : p));
    }, [effectiveMuted, isDeafened, currentUser]);

    // Handle deafen audio tags override
    useEffect(() => {
        // Handled automatically via prop bindings, no extra local audioRef mute required
    }, [isDeafened]);

    const toggleCamera = async () => {
        try {
            const targetState = !isCameraOn;
            if (roomRef.current && roomRef.current.state === 'connected') {
                await roomRef.current.localParticipant.setCameraEnabled(targetState, {
                    facingMode: facingMode
                });
                
                // Get local stream from LiveKit local participant tracks
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                } else {
                    setLocalStream(null);
                }
            } else {
                // Fallback: Local preview mode when LiveKit is not connected
                console.warn('[LiveKit] Room not connected. Toggling camera in local preview mode.');
                if (targetState) {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
                    const videoTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(videoTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                } else {
                    if (localStream) {
                        localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                        setLocalStream(new MediaStream(localStream.getTracks()));
                    }
                }
            }
            setIsCameraOn(targetState);
            setIsScreenSharing(false);
            socket.emit('voice-state-update', { isCameraOn: targetState, isScreenSharing: false });
        } catch (err) {
            console.error('[LiveKit] Camera toggle error:', err);
        }
    };

    // Update camera facingMode
    useEffect(() => {
        const updateFacing = async () => {
            if (roomRef.current && roomRef.current.state === 'connected' && roomRef.current.localParticipant.isCameraEnabled) {
                await roomRef.current.localParticipant.setCameraEnabled(true, {
                    facingMode: facingMode
                });
                
                // Get local stream from LiveKit local participant tracks
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                }
            } else if (isCameraOn) {
                // Fallback local preview flip: restart camera with new facingMode
                try {
                    console.log('[LiveKit] Flipping camera in fallback preview mode to:', facingMode);
                    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
                    const videoTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(videoTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                } catch (err) {
                    console.error('[LiveKit] Fallback camera flip error:', err);
                }
            }
        };
        updateFacing();
    }, [facingMode]);

    const toggleScreenShare = async () => {
        try {
            const targetState = !isScreenSharing;
            if (roomRef.current && roomRef.current.state === 'connected') {
                await roomRef.current.localParticipant.setScreenShareEnabled(targetState);
                
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                } else {
                    setLocalStream(null);
                }
            } else {
                console.warn('[LiveKit] Room not connected. Toggling screen share in local preview mode.');
                if (targetState) {
                    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
                    const screenTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(screenTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                    
                    screenTrack.onended = () => {
                        setIsScreenSharing(false);
                        socket.emit('voice-state-update', { isScreenSharing: false });
                        if (localStream) {
                            localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                            setLocalStream(new MediaStream(localStream.getTracks()));
                        }
                    };
                } else {
                    if (localStream) {
                        localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                        setLocalStream(new MediaStream(localStream.getTracks()));
                    }
                }
            }
            setIsScreenSharing(targetState);
            setIsCameraOn(false);
            socket.emit('voice-state-update', { isScreenSharing: targetState, isCameraOn: false });
        } catch (err) {
            console.error('[LiveKit] Screen share toggle error:', err);
        }
    };

    const focusedOverlayNode = focusedParticipant && (
        <div 
            className="fixed inset-0 z-[150] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-4 animate-fade-in"
            onClick={() => setFocusedParticipant(null)}
        >
            <div className="absolute top-4 right-4 z-[160]">
                <button 
                    type="button" 
                    onClick={() => setFocusedParticipant(null)}
                    className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all active:scale-95 shadow-lg"
                >
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
            
            <div className={`w-full h-full flex items-center justify-center ${focusedParticipant.isScreenSharing ? 'max-w-full max-h-full' : 'max-w-2xl max-h-[70vh] rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl bg-black/50'}`}>
                {(() => {
                    const participantStream = focusedParticipant.username === currentUser 
                        ? localStream 
                        : remoteStreamsMap[focusedParticipant.socketId];
                    return (
                        <RemoteStreamView 
                            stream={participantStream}
                            className="w-full h-full object-contain"
                        />
                    );
                })()}
            </div>
            
            <div className="absolute bottom-6 px-4 py-2 rounded-full bg-black/60 border border-white/10 backdrop-blur-sm text-white text-sm font-bold shadow-lg">
                {focusedParticipant.isScreenSharing ? `📺 Screen Share: ${focusedParticipant.username}` : `📷 Camera: ${focusedParticipant.username}`}
            </div>
        </div>
    );

    const participantsDest = document.getElementById('voice-participants-destination');
    const controlsDest = document.getElementById('voice-controls-destination');

    const participantsNode = participantsDest ? (
        <div className="flex items-center gap-2 overflow-x-auto p-2 scrollbar-hide">
            {participants.map(p => {
                const userData = usersData[p.username] || { displayName: p.username, pfp: '' };
                const activeThemeColor = (userData as any).appThemeColor || themeColor;
                const hasVideo = !!(p.isCameraOn || p.isScreenSharing);
                const participantStream = p.username === currentUser ? localStream : remoteStreamsMap[p.socketId];

                return (
                    <div key={p.socketId} className="relative flex-shrink-0">
                        <ParticipantCard
                            p={p}
                            userData={userData}
                            activeThemeColor={activeThemeColor}
                            hasVideo={hasVideo}
                            participantStream={participantStream}
                            currentUser={currentUser}
                            isDeafened={isDeafened}
                            small={true}
                            onClick={() => setFocusedParticipant(p)}
                        />
                    </div>
                );
            })}
        </div>
    ) : (
        <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-x-4 gap-y-10">
                {participants.map(p => {
                    const userData = usersData[p.username] || { displayName: p.username, pfp: '' };
                    const activeThemeColor = (userData as any).appThemeColor || themeColor;
                    const hasVideo = !!(p.isCameraOn || p.isScreenSharing);
                    const participantStream = p.username === currentUser ? localStream : remoteStreamsMap[p.socketId];

                    return (
                        <ParticipantCard
                            key={p.socketId}
                            p={p}
                            userData={userData}
                            activeThemeColor={activeThemeColor}
                            hasVideo={hasVideo}
                            participantStream={participantStream}
                            currentUser={currentUser}
                            isDeafened={isDeafened}
                            onClick={() => setFocusedParticipant(p)}
                        />
                    );
                })}
            </div>
        </div>
    );

    const controlsNode = (
        <div className="w-full overflow-x-hidden">
            <div className="max-w-2xl mx-auto flex flex-wrap items-center justify-center gap-3 sm:gap-6">
                <button
                    onClick={() => setIsMuted(!isMuted)}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isMuted ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                    title={isMuted ? "Unmute Mic" : "Mute Mic"}
                >
                    {isMuted ? <VolumeOffIcon className="w-6 h-6" /> : <MicIcon className="w-6 h-6" />}
                </button>

                <button
                    onClick={() => {
                        const nextDeafen = !isDeafened;
                        setIsDeafened(nextDeafen);
                        if (nextDeafen) {
                            setIsMuted(true);
                        }
                    }}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isDeafened ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                    title={isDeafened ? "Undeafen Audio" : "Deafen Audio"}
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                        {isDeafened && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />}
                    </svg>
                </button>

                <button
                    onClick={toggleCamera}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isCameraOn ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                >
                    <CameraIcon className="w-6 h-6" />
                </button>

                {isCameraOn && (
                    <button
                        onClick={() => setFacingMode(prev => prev === 'user' ? 'environment' : 'user')}
                        className="p-4 text-raised bg-white/5 text-gray-300 hover:bg-white/10 rounded-full transition-all duration-300"
                    >
                        <FlipCameraIcon className="w-6 h-6" />
                    </button>
                )}

                <button
                    onClick={toggleScreenShare}
                    className={`p-4 rounded-full transition-all duration-300 ${isScreenSharing ? 'bg-green-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                >
                    <ScreenShareIcon className="w-6 h-6" />
                </button>

                <button
                    onClick={() => onLeave ? onLeave() : window.location.reload()}
                    className="p-4 text-raised bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-300"
                >
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
        </div>
    );

    if (participantsDest && controlsDest) {
        return (
            <>
                {createPortal(participantsNode, participantsDest)}
                {createPortal(controlsNode, controlsDest)}
                {focusedOverlayNode}
            </>
        );
    }

    return (
        <div className="flex flex-col h-full rounded-2xl m-4 overflow-hidden">
            {participantsNode}
            <div className=" p-6">
                {controlsNode}
            </div>
            {focusedOverlayNode}
        </div>
    );
};

const ParticipantCard = React.memo(({ p, userData, activeThemeColor, hasVideo, participantStream, currentUser, isDeafened, small, onClick }: {
    p: VoiceParticipant;
    userData: any;
    activeThemeColor: string;
    hasVideo: boolean;
    participantStream: MediaStream | null;
    currentUser: string;
    isDeafened: boolean;
    small?: boolean;
    onClick?: () => void;
}) => {
    return (
        <div className={`flex flex-col items-center gap-2 animate-fade-in group ${small ? 'w-auto' : ''}`}>
            <div
                onClick={hasVideo ? onClick : undefined}
                className={`relative ${small ? 'w-10 h-10 border-2' : 'w-24 h-24 border-4'} rounded-full overflow-hidden transition-all duration-300 transform group-hover:scale-[1.05] ${p.isSpeaking ? 'scale-110' : 'scale-100'} ${hasVideo ? 'cursor-pointer' : ''}`}
                style={{ 
                    borderColor: p.isSpeaking ? activeThemeColor : 'rgba(255,255,255,0.1)',
                    boxShadow: p.isSpeaking ? `0 0 20px ${activeThemeColor}44` : 'none',
                    backgroundColor: '#111827',
                    isolation: 'isolate',
                    WebkitMaskImage: '-webkit-radial-gradient(white, black)'
                }}
            >
                {hasVideo && participantStream ? (
                    <RemoteStreamView
                        stream={participantStream}
                        isLocal={p.username === currentUser}
                        className="w-full h-full object-cover rounded-full"
                        style={{
                            WebkitMaskImage: '-webkit-radial-gradient(white, black)'
                        }}
                    />
                ) : (
                    userData.pfp ? (
                        <img
                            src={userData.pfp}
                            alt={p.username}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 text-white text-3xl font-bold">
                            {p.username[0]?.toUpperCase()}
                        </div>
                    )
                )}

                {/* Status Indicators Overlay */}
                <div className="absolute inset-0 pointer-events-none ring-inset ring-1 ring-white/10 rounded-full" />
            </div>

            {!small && (
                <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/40">
                        <span className="text-[10px] font-black text-white/90 truncate max-w-[80px] uppercase tracking-wider">
                            {userData.displayName || p.username}
                        </span>
                        {p.isDeafened && (
                            <svg className="w-2.5 h-2.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 3l18 18" />
                            </svg>
                        )}
                        {p.isMuted && !p.isDeafened && <VolumeOffIcon className="w-2.5 h-2.5 text-red-500" />}
                    </div>
                </div>
            )}
            
            {small && (p.isMuted || p.isDeafened) && (
                <div className="absolute -bottom-1 -right-1 bg-black/80 rounded-full p-0.5 border border-white/10 z-10 flex gap-0.5">
                    {p.isDeafened ? (
                        <svg className="w-2.5 h-2.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 3l18 18" />
                        </svg>
                    ) : (
                        <VolumeOffIcon className="w-2.5 h-2.5 text-red-500" />
                    )}
                </div>
            )}

            {p.username !== currentUser && participantStream && (
                <RemoteAudioPlayer stream={participantStream} muted={isDeafened} />
            )}
        </div>
    );
});
ParticipantCard.displayName = 'ParticipantCard';
